import { forwardRef, useEffect, useImperativeHandle, useMemo, useRef } from 'react'
import { useShapeSpellActiveWord } from '@/components/app/shapes/shape-spell-active-word'
import { useShapeSpellPeek } from '@/components/app/shapes/shape-spell-peek'
import { useShapeSpellUnderlines } from '@/components/app/shapes/shape-spell-underlines'
import { computeSelectionRects } from '@/lib/shapes/shape-text'
import type { TextRunStyleUpdate } from '@/lib/text/types/text-run-style-update'
import { DropCapGlyph } from './components/drop-cap-glyph'
import { ShapeTextCaret } from './components/shape-text-caret'
import { ShapeTextChunk } from './components/shape-text-chunk'
import { ShapeTextPeek } from './components/shape-text-peek'
import { ShapeTextPlaceholder } from './components/shape-text-placeholder'
import { SpellUnderlines } from './components/spell-underlines'
import { SpellWordAnchor } from './components/spell-word-anchor'
import { TextSelectionHighlights } from './components/text-selection-highlights'
import { layoutPlaceholderChunks } from './helpers/placeholder-layout'
import { peekContentHeight } from './helpers/peek-content-height'
import { hasVisibleOverset } from './helpers/visible-overset'
import { useChainOverflowHandoff } from './hooks/use-chain-overflow-handoff'
import { useChainTrimResync } from './hooks/use-chain-trim-resync'
import { useParagraphEmitter } from './hooks/use-paragraph-emitter'
import { useShapeTextCaret } from './hooks/use-shape-text-caret'
import { useShapeTextCommands } from './hooks/use-shape-text-commands'
import { useShapeTextEditing } from './hooks/use-shape-text-editing'
import { useShapeTextFrameRegistration } from './hooks/use-shape-text-frame-registration'
import { useShapeTextKeyboard } from './hooks/use-shape-text-keyboard'
import { useShapeTextLayout } from './hooks/use-shape-text-layout'
import { useShapeTextPaste } from './hooks/use-shape-text-paste'
import { useShapeTextSelection } from './hooks/use-shape-text-selection'
import { useShapeTextStyle } from './hooks/use-shape-text-style'
import type { ShapeTextEditorProps } from './types/shape-text-editor-props'
import type { ShapeTextEditorRef } from './types/shape-text-editor-ref'

export type { ShapeTextEditorRef }

/**
 * Edits and paints one shape's text: a hidden `<textarea>` owns input and selection, and the
 * visible text, caret and highlight are painted from the shape-text layout.
 *
 * The hooks run in a fixed order — the order this code originally had in one component — because
 * several effects depend on it (the trim-resync must run before the editing hook's caret-init, the
 * caret snap after everything that can move the caret).
 */
export const ShapeTextEditor = forwardRef<ShapeTextEditorRef, ShapeTextEditorProps>(
  (
    {
      shape,
      paragraphs,
      isEditing,
      canvasZoom,
      initialCaretClientPoint,
      initialCaretOffset,
      onParagraphsChange,
      onExitEdit,
      onContextMenu,
      suppressExitOnBlur = false,
      chainFrameIds,
      onChainSelectionChange,
      chainSelectionRange,
      onChainSelectionReplace,
      onChainSelectionExtend,
      hasChainSelection = false,
      hasNextInChain = false,
      hasPrevInChain = false,
      opacityMultiplier = 1,
      wrap,
      onChainBackspaceAtStart,
      onChainArrowLeftAtStart,
      onChainArrowRightAtEnd,
      onChainOverflowCaret,
      onOverflowChange,
      onSelectChain,
      onChainClipboard,
    },
    ref
  ) => {
    const textareaRef = useRef<HTMLTextAreaElement | null>(null)
    const overlayRef = useRef<HTMLDivElement | null>(null)
    const caretInitializedRef = useRef(false)
    const isEditingRef = useRef(isEditing)
    const pendingRunStyleRef = useRef<TextRunStyleUpdate | null>(null)

    isEditingRef.current = isEditing

    const {
      textValue,
      setTextValue,
      charRunMap,
      flow,
      layout,
      capBoxes,
      measureTextPt,
      fonts,
      pointSize,
      rowMetricPt,
      familyId,
      cssFamily,
      fontWeight,
      fontStyle,
    } = useShapeTextLayout({ shape, paragraphs, isEditing, hasNextInChain, wrap })

    // A drop cap's own character is emitted with a forced advance (`dropCapOverrides`) so caret math
    // stays correct by construction, but it still merges into its normal per-character chunk/span like
    // any other glyph — see `shape-drop-cap.ts`'s `layoutShapeTextWithDropCap` doc comment. That span
    // is hidden (not removed — removing it would shrink its box and shift every glyph after it left)
    // and the enlarged glyph is painted separately, positioned by `capBoxes` (`DropCapGlyph`).
    const dropCapCharIndices = useMemo(
      () => new Set(capBoxes.map((box) => box.charIndex)),
      [capBoxes]
    )

    // Only computed while there's nothing real to show — see `packPlaceholderChars` for why this
    // reuses `flow`/`measureTextPt` (the same ones real text and the caret use) rather than the
    // shared word-wrap engine.
    const placeholderChunks = useMemo(
      () =>
        textValue.length > 0
          ? []
          : layoutPlaceholderChunks(flow.rows, flow.fallbackRow, measureTextPt),
      [flow.fallbackRow, flow.rows, measureTextPt, textValue.length]
    )

    const { lastEmittedModelTextRef, emitSeqRef, emitParagraphs } =
      useParagraphEmitter(onParagraphsChange)

    const { chainOverflowRelocatedRef } = useChainOverflowHandoff({
      isEditing,
      hasNextInChain,
      onChainOverflowCaret,
      consumedLength: layout.consumedLength,
      textValue,
      textareaRef,
      emitSeqRef,
    })

    const charRunMapRef = useRef(charRunMap)

    charRunMapRef.current = charRunMap
    const fontsRef = useRef(fonts)

    fontsRef.current = fonts

    const {
      caretIndex,
      setCaretIndex,
      setSelectionEndIndex,
      setSelectionForward,
      syncSelectionFromTextarea,
      handlePointerDown,
      handlePointerUp,
      activeCaret,
      selectionRects,
    } = useShapeTextSelection({
      shape,
      layout,
      flow,
      isEditing,
      canvasZoom,
      overlayRef,
      textareaRef,
      pendingRunStyleRef,
      chainFrameIds,
      onChainSelectionChange,
      chainSelectionRange,
    })

    // Misspelled words the Spell Check panel is underlining in this frame. Only text the layout
    // drew has carets, so words in overflow produce no rectangles and are not underlined.
    const spellRanges = useShapeSpellUnderlines(shape.id)
    const spellUnderlineRects = useMemo(
      () =>
        spellRanges.flatMap((range) =>
          computeSelectionRects(
            layout.carets,
            range.start,
            Math.min(range.end, layout.consumedLength)
          )
        ),
      [spellRanges, layout.carets, layout.consumedLength]
    )

    // The word the Spell Check popover is open on, when it is one this frame draws. The popover opens
    // under it, with or without squiggles, so it is marked here on its own (`SpellWordAnchor`). A word
    // in hidden text is marked by the peek instead.
    const activeSpellWord = useShapeSpellActiveWord(shape.id)
    const activeSpellWordRect = useMemo(
      () =>
        activeSpellWord && activeSpellWord.end <= layout.consumedLength
          ? (computeSelectionRects(layout.carets, activeSpellWord.start, activeSpellWord.end)[0] ??
            null)
          : null,
      [activeSpellWord, layout.carets, layout.consumedLength]
    )

    useChainTrimResync({
      isEditing,
      hasNextInChain,
      hasPrevInChain,
      paragraphs,
      textValue,
      setTextValue,
      textareaRef,
      caretInitializedRef,
      initialCaretOffset,
      chainOverflowRelocatedRef,
      lastEmittedModelTextRef,
      emitSeqRef,
      setCaretIndex,
      setSelectionEndIndex,
    })

    const visibleOverset = useMemo(
      () => hasVisibleOverset(textValue, layout.consumedLength),
      [layout.consumedLength, textValue]
    )

    // Overflow means a LAST-in-chain frame holding INKED text the engine will not draw (WMPCM-9338)
    // — see `hasVisibleOverset` for why the ink test rather than a length comparison. Over-capacity
    // input is accepted rather than refused, so there is no separate "refusing further input"
    // signal left to OR in — that used to be `didBlockOverflow`/`isAtCapacity`, both gone with the
    // hard-block.
    //
    // ONE value feeds both consumers: the registry's `isShowingOverflowBadge` (the text-linking
    // layer suppresses the out-port "+" container on it) and `onOverflowChange` (the visual layer's
    // `TextOverflowBadgeLayer` paints the badge from it). They used to be two equal expressions with
    // comments insisting they stay equal; this makes that true by construction. It once carried an
    // extra `isSelected || isEditing` gate, mirroring the visual layer's; both are gone, because
    // hiding overflow until the frame happens to be selected is how text goes missing unnoticed. If
    // a visibility rule ever comes back it has to apply to this one value, or the out-port "+" and
    // the badge will contradict each other.
    const isOverflowing = visibleOverset && !hasNextInChain

    // The Spell Check panel asks for a peek while its popover is open on a misspelling in this
    // frame's hidden text. Only a frame that really hides text peeks, and the peek is paint only:
    // it adds the hidden tail below the frame and leaves the frame's own layout, badge and
    // registered handle describing the real frame.
    const isPeeking = useShapeSpellPeek(shape.id) && isOverflowing

    useShapeTextFrameRegistration({
      deps: {
        layout,
        shape,
        canvasZoom,
        textValue,
        charRunMap,
        paragraphs,
        showsOverflowBadge: isOverflowing,
        editingCaretIndex: isEditing ? caretIndex : null,
      },
      overlayRef,
      fontsRef,
      setTextValue,
      lastEmittedModelTextRef,
    })

    const { isFocused, setIsFocused, handleTextChange, handleBlur } = useShapeTextEditing({
      shape,
      paragraphs,
      isEditing,
      initialCaretClientPoint,
      initialCaretOffset,
      canvasZoom,
      layout,
      textValue,
      setTextValue,
      onParagraphsChange: emitParagraphs,
      onExitEdit,
      pendingRunStyleRef,
      textareaRef,
      overlayRef,
      caretInitializedRef,
      isEditingRef,
      setCaretIndex,
      setSelectionEndIndex,
      suppressExitOnBlur,
    })

    // The badge steps aside while the peek is open: the hidden text it stands for is on show, and the
    // badge would sit on top of it. Only the painted badge changes; `isOverflowing` itself, which the
    // registered handle reports to the spell scan, does not.
    const showsBadge = isOverflowing && !isPeeking

    useEffect(() => {
      onOverflowChange?.(shape.id, showsBadge)

      return () => onOverflowChange?.(shape.id, false)
    }, [shape.id, showsBadge, onOverflowChange])

    const {
      applyRunStyle,
      getEditingStyle,
      applyKerningAtCaret,
      getKerningState,
      markPointSizeBoundaryHit,
    } = useShapeTextStyle({
      charRunMap,
      paragraphs,
      shape,
      carets: layout.carets,
      consumedLength: layout.consumedLength,
      onParagraphsChange: emitParagraphs,
      textareaRef,
      pendingRunStyleRef,
    })

    const { execCopy, execCut, execSelectAll, execDelete } = useShapeTextCommands({
      textareaRef,
      charRunMap,
      paragraphs,
      fontsRef,
      pendingRunStyleRef,
      setCaretIndex,
      setSelectionEndIndex,
      setSelectionForward,
    })

    const { handlePaste, execPaste } = useShapeTextPaste({
      shapeId: shape.id,
      paragraphs,
      textareaRef,
      charRunMapRef,
      fontsRef,
      pendingRunStyleRef,
      chainSelectionRange,
      onChainSelectionReplace,
      setTextValue,
      emitParagraphs,
      setCaretIndex,
      setSelectionEndIndex,
    })

    const handleKeyDown = useShapeTextKeyboard({
      shapeId: shape.id,
      carets: layout.carets,
      hasChainSelection,
      hasNextInChain,
      hasPrevInChain,
      textareaRef,
      charRunMapRef,
      fontsRef,
      getEditingStyle,
      applyRunStyle,
      markPointSizeBoundaryHit,
      execSelectAll,
      execCopy,
      execCut,
      chainFrameIds,
      onChainSelectionChange,
      onChainSelectionReplace,
      onChainSelectionExtend,
      onChainBackspaceAtStart,
      onChainArrowLeftAtStart,
      onChainArrowRightAtEnd,
      onExitEdit,
      onSelectChain,
      onChainClipboard,
    })

    const { caretRef, caretInk, rowBaselinePt, caretRowHeightPt, caretWidthPt } = useShapeTextCaret(
      {
        charRunMap,
        chunks: layout.chunks,
        caretIndex,
        paragraphs,
        pointSize,
        familyId,
        fonts,
        canvasZoom,
        caretX: activeCaret?.x,
        caretYTop: activeCaret?.yTop,
        caretRowHeight: activeCaret?.rowHeight,
        rowMetricPt,
        shapeRotation: shape.rotation,
        isFocused,
      }
    )

    useImperativeHandle(
      ref,
      () => ({
        applyRunStyle,
        focus: () => {
          // `preventScroll`: the textarea is `opacity-0` and can sit off-screen at high zoom, so a
          // plain focus() yanks the canvas viewport to it.
          textareaRef.current?.focus({ preventScroll: true })
        },
        getEditingStyle,
        applyKerningAtCaret,
        getKerningState,
        execCopy,
        execCut,
        execDelete,
        execPaste,
        execSelectAll,
      }),
      [
        applyRunStyle,
        getEditingStyle,
        applyKerningAtCaret,
        getKerningState,
        execCopy,
        execCut,
        execDelete,
        execPaste,
        execSelectAll,
      ]
    )

    return (
      <div
        className="absolute"
        data-shape-text-overlay="true"
        data-shape-id={shape.id}
        style={{
          left: shape.x,
          top: shape.y,
          width: shape.width,
          height: shape.height,
          transform: shape.rotation ? `rotate(${shape.rotation}deg)` : undefined,
          transformOrigin: 'center center',
          pointerEvents: isEditing ? 'auto' : 'none',
          // A peek runs past the frame's edge, so it rises above its neighbours like an edit does.
          zIndex: isEditing || isPeeking ? 52 : undefined,
          cursor: isEditing ? 'text' : undefined,
          opacity: opacityMultiplier < 1 ? opacityMultiplier : undefined,
        }}
        onPointerDown={handlePointerDown}
        onPointerUp={handlePointerUp}
        onContextMenu={(e) => {
          if (!isEditing) return
          e.preventDefault()
          e.stopPropagation()
          // Safari/macOS reports Ctrl+click as a primary-button `pointerdown` (which arms the drag
          // anchor) and then swallows the matching `pointerup` once the context menu appears. The
          // anchor would stay armed, so every later `pointermove` re-extended the selection and
          // overwrote a menu Select All (WMPCM-11348). The menu means the press is over.
          handlePointerUp()
          const ta = textareaRef.current

          onContextMenu?.({
            clientX: e.clientX,
            clientY: e.clientY,
            isSelectionCollapsed: ta ? ta.selectionStart === ta.selectionEnd : true,
          })
        }}
      >
        <div
          ref={overlayRef}
          className="absolute inset-0 overflow-hidden"
          style={{
            // The fill-rule goes OUTSIDE the quotes: the grammar is `path( [<fill-rule>,]? <string> )`.
            // Emitted as `path('evenodd, M …')` the quoted string is not valid path data, so the whole
            // declaration was invalid and dropped — no outline clip applied at all, leaving only
            // Tailwind's `overflow-hidden`, which clips to the bounding BOX. For a star or heart the
            // bbox is far larger than the fill, so large glyphs painted outside the visible shape and
            // over its neighbours (WMPCM-9070). Still load-bearing, but no longer because the
            // layout leans on it: real text stopped placing a glyph too wide for its segment
            // (WMPCM-10123). It now backstops the things that are still allowed to exceed their box
            // — a drop cap's enlarged glyph, the empty-frame placeholder's "place one character
            // anyway" row, an over-tall glyph box, and the caret.
            clipPath: `path(evenodd, '${flow.pathD}')`,
          }}
        >
          {!isEditing && textValue.length === 0 && (
            <ShapeTextPlaceholder
              chunks={placeholderChunks}
              shapeId={shape.id}
              rowHeight={flow.rowHeight}
              pointSize={pointSize}
              fontWeight={fontWeight}
            />
          )}
          {layout.chunks.map((chunk, chunkIdx) => (
            <ShapeTextChunk
              key={`shape-text-chunk-${shape.id}-${chunkIdx}`}
              chunk={chunk}
              charRunMap={charRunMap}
              advances={layout.advances}
              dropCapCharIndices={dropCapCharIndices}
              fonts={fonts}
              familyId={familyId}
              cssFamily={cssFamily}
              fontWeight={fontWeight}
              fontStyle={fontStyle}
              pointSize={pointSize}
              rowMetricPt={rowMetricPt}
            />
          ))}
          {capBoxes.map((box) => (
            <DropCapGlyph
              key={`shape-text-dropcap-${shape.id}-${box.charIndex}`}
              box={box}
              run={charRunMap[box.charIndex] ?? null}
              fonts={fonts}
              familyId={familyId}
              cssFamily={cssFamily}
              fontWeight={fontWeight}
              fontStyle={fontStyle}
            />
          ))}
          {spellUnderlineRects.length > 0 && <SpellUnderlines rects={spellUnderlineRects} />}
          {activeSpellWord && activeSpellWordRect && (
            <SpellWordAnchor
              rect={activeSpellWordRect}
              range={`${activeSpellWord.start}-${activeSpellWord.end}`}
            />
          )}
          {/* `|| chainSelectionRange` so a chain member that is NOT the editing frame still paints
              its share of a cross-frame selection (WMPCM-9199). Only one frame in a chain is ever
              editing, so gating the highlight on `isEditing` alone made a selection stop dead at the
              frame boundary no matter how far the drag actually reached. */}
          {(isEditing || chainSelectionRange != null) && selectionRects.length > 0 && (
            <TextSelectionHighlights rects={selectionRects} />
          )}
          {/* Removed (WMPCM-9070): a per-glyph-edge magenta tick overlay used to render here while
              editing, gated on `isDebugEnabled()` — which is true on dev/local/staging and defaults to
              true when unset, so it painted wherever QA tests and was filed as "thin purple lines
              appear between the letters". It was a caret-drift diagnostic for the WMPCM-8156 →
              WMPCM-8161 → WMPCM-8970 chain; drift is now covered by `caretIndexAtShapeLocalPoint`'s
              tests and the per-glyph advance boxes (`ShapeTextChunk`), so the overlay isn't carried forward. Recover
              it from git history if a future caret-drift bug needs eyes on it again. */}
          {isEditing && isFocused && activeCaret && (
            <ShapeTextCaret
              // Keyed on the caret's own position so moving it (typing, arrow keys, a click)
              // remounts the bar and restarts the blink from solid — the same "just moved, so
              // hold visible for a beat" feel a native text caret has, instead of blinking on a
              // fixed clock that's out of phase with the last edit.
              key={`${activeCaret.x}-${activeCaret.yTop}`}
              ref={caretRef}
              caret={activeCaret}
              widthPt={caretWidthPt}
              ink={caretInk}
              baselinePt={rowBaselinePt}
              rowHeightPt={caretRowHeightPt}
              shapeHeight={shape.height}
            />
          )}
        </div>
        {isPeeking && (
          <ShapeTextPeek
            shape={shape}
            paragraphs={paragraphs}
            consumedLength={layout.consumedLength}
            drawnBottom={peekContentHeight(layout.chunks, 0)}
          />
        )}
        {isEditing && (
          <textarea
            ref={textareaRef}
            value={textValue}
            // WMPCM-10141: `opacity-0` alone is an implicit way to hide this textarea's native
            // text selection — `selection:bg-transparent` makes that explicit, so there is nothing
            // for a browser/OS selection-rendering quirk (reported on macOS) to paint even if it
            // does not fully respect the element's opacity. The visible highlight is always the
            // separate `selectionRects` overlay above, not this textarea's own selection.
            className="pointer-events-none absolute inset-0 resize-none border-0 bg-transparent p-0 opacity-0 outline-none selection:bg-transparent"
            onChange={handleTextChange}
            onPaste={handlePaste}
            onBlur={handleBlur}
            onFocus={() => setIsFocused(true)}
            onKeyDown={handleKeyDown}
            onKeyUp={syncSelectionFromTextarea}
            onSelect={syncSelectionFromTextarea}
          />
        )}
      </div>
    )
  }
)

ShapeTextEditor.displayName = 'ShapeTextEditor'
